import React from "react";
import { SquarePlay, Video, CheckCircle, ExternalLink } from "lucide-react";
import { Card, Button, Skeleton } from "@/components/ui";
import { Review, ReviewSentiment } from "@/services/bedrock";

type VideoReplyConfig = {
  title: string;
  description: string;
  badges: string[];
};

const videoReplyConfigs: Record<ReviewSentiment, VideoReplyConfig> = {
  positive: {
    title: "Send a Video Thank You",
    description: "Stand out by sending a personalized video response.",
    badges: ["Builds Relationships", "Drives Referrals"],
  },
  neutral: {
    title: "Send a Video Follow-Up",
    description: "Stand out by sending a personalized video response.",
    badges: ["Shows You Care", "Gathers Feedback"],
  },
  negative: {
    title: "Send a Video Response",
    description: "Address concerns directly with a personalized video response.",
    badges: ["De-Escalates", "Shows Empathy"],
  },
};

interface ReviewVideoReplyCardProps {
  review: Review;
  sentiment: ReviewSentiment;
  onRecordResponse: () => void;
  onViewExistingResponse: (videoPageId: number) => void;
  isLoadingDetails?: boolean;
}

export function ReviewVideoReplyCard({
  review,
  sentiment,
  onRecordResponse,
  onViewExistingResponse,
  isLoadingDetails = false,
}: ReviewVideoReplyCardProps) {
  const videoReplyConfig = videoReplyConfigs[sentiment] || videoReplyConfigs.neutral;
  const hasLatestPage = !!review.latestContentPage;
  const reviewName = review.displayName?.split(" ")?.[0] || "this review";

  // Render a matching skeleton overlay if details are loading AND we know a reply exists (review.replied === true)
  // This prevents the card from temporarily flickering/jumping to the unrecorded state!
  if (isLoadingDetails && review.replied && !hasLatestPage) {
    return (
      <Card className="flex flex-col p-6 md:p-8 border border-slate-200/80 bg-white rounded-2xl relative shadow-sm animate-pulse shrink-0 min-h-[150px] justify-center">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <Skeleton className="size-12 rounded-2xl shrink-0 hidden sm:flex" />
            <div className="space-y-2.5 flex-1 min-w-[200px]">
              <Skeleton className="h-5 w-40 rounded" />
              <Skeleton className="h-4 w-full max-w-sm rounded" />
            </div>
          </div>
          <div className="flex flex-col gap-2 w-full md:w-48 shrink-0">
            <Skeleton className="h-10 w-full rounded-lg" />
            <Skeleton className="h-10 w-full rounded-lg" />
          </div>
        </div>
      </Card>
    );
  }

  if (hasLatestPage) {
    const videoPageId = review.latestContentPage?.id || 0;

    return (
      <Card className="flex flex-col p-6 md:p-8 border-2 border-emerald-500/70 bg-linear-to-br from-emerald-50/40 via-white to-white rounded-2xl relative shadow-sm transition-all duration-300">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mt-4 md:mt-0">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-2xl border border-emerald-200 bg-emerald-100/50 text-emerald-600 shrink-0 shadow-inner hidden sm:flex">
              <CheckCircle className="h-6 w-6" />
            </div>
            <div className="flex flex-col gap-1">
              <div className="font-bold text-lg text-slate-900 tracking-tight">Your video response is live!</div>
              <p className="text-sm text-slate-500 font-medium">
                You've recorded a custom video message for {reviewName}. You can view or share the generated video page,
                or record a fresh take anytime.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-2 w-full md:w-48 shrink-0 mt-2 md:mt-0">
            <Button
              onClick={() => onViewExistingResponse(videoPageId)}
              variant="outline"
              className="w-full font-bold flex items-center justify-center gap-2 text-sm h-10 px-5 border-emerald-500 border-2 text-emerald-500"
            >
              <ExternalLink className="h-4 w-4" />
              Share Video Page
            </Button>

            <Button
              onClick={onRecordResponse}
              variant="outline"
              className="w-full font-bold flex items-center justify-center gap-2 text-sm h-10 px-5 border-slate-200 hover:bg-slate-50 text-slate-700"
            >
              <Video className="h-4 w-4" />
              Record New Take
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card
      onClick={onRecordResponse}
      className="flex flex-col p-6 md:p-8 border border-slate-200 bg-white rounded-2xl relative shadow-sm hover:bg-slate-50/50 hover:border-slate-300 transition-all duration-300 cursor-pointer"
    >
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div className="flex items-start gap-4 mt-4 md:mt-0">
          <div className="p-3 rounded-2xl border border-slate-200 bg-slate-50 text-slate-400 shrink-0 shadow-inner hidden sm:flex">
            <SquarePlay className="h-6 w-6" />
          </div>
          <div className="flex flex-col gap-1">
            <div className="font-bold text-lg text-slate-900 tracking-tight">{videoReplyConfig.title}</div>
            {/* Outcome Badges */}
            {videoReplyConfig.badges.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {videoReplyConfig.badges.map((badge, index) => (
                  <div
                    key={index}
                    className="bg-primary/5 text-primary border border-primary/10 px-2 py-0.5 rounded-md text-[12px] font-bold uppercase tracking-wider"
                  >
                    {badge}
                  </div>
                ))}
              </div>
            )}

            <div className="text-sm text-slate-500 font-medium mt-3">
              Takes about <span className="font-bold text-slate-700">1-2 minutes</span>. Just read the teleprompter!
            </div>
          </div>
        </div>

        <Button
          variant="outline"
          className="w-full md:w-auto shrink-0 font-bold flex items-center justify-center gap-2 text-sm h-10 px-5 border-primary border-2 text-primary hover:bg-primary/5"
          onClick={e => {
            // Prevent Card onClick double trigger
            e.stopPropagation();
            onRecordResponse();
          }}
        >
          <Video className="h-4 w-4" />
          Review Script & Record
        </Button>
      </div>
    </Card>
  );
}
